import { useLazyQuery } from "@apollo/client";
import {
  makeStyles,
  Theme,
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Container,
  Fab,
} from "@material-ui/core";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { GET_BOOKING } from "../../../../graphql/bookings/getBookingQuery";
import { IAddressLocation, ICustomer } from "../../../../reducers/bookings/types";
import { IAppState } from "../../../../store";
import { getLocalizedBookingSyntex } from "../../../../utils/localized.syntex";
import { TopBar } from "../../../TopBar/TopBar";
import { DateTime as d } from "luxon";
import { ITestDriveBookingDetails } from "../../../../reducers/testDriveBooking/types";
import { CustomerType } from "../../../../reducers/auth/types";
import { socket } from "../../../../utils/socket";
import { SOCKET_EVENTS } from "../../utils";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(3),
    margin: "0 1rem",
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  label: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    marginBottom: theme.spacing(0.5),
  },
  value: {
    fontSize: "1rem",
  },
  header: {
    marginBottom: theme.spacing(2),
  },
  bottomBar: {
    height: "60px",
    backgroundColor: "#fff",
    boxShadow: "0 0 20px 1px rgba(0,0,0,0.06)",
    padding: "8px 60px",
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    flex: 1,
    overflow: "hidden",
    justifyContent: "center",
    zIndex: 900,
  },
}));

interface ITestDriveBookingSummary {
  id: string;
  bookingReference: string;
  vehicle: string;
  testDriveBookingDetails: ITestDriveBookingDetails;
  branchName: string;
  branchAddress: string;
  bookingTime: string;
  customer: ICustomer;
  pickupOtherLocation: IAddressLocation;
}

const TestDriveBookingConfirmation: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const [testDriveBookingSummary, setTestDriveBookingSummary] =
    useState<ITestDriveBookingSummary>();
  const organisation = useSelector(
    (state: IAppState) =>
      state.consumerWebsiteReducer.consumerWebsite.organisation
  );
  const branchData = useSelector(
    (state: IAppState) => state.consumerWebsiteReducer.consumerWebsite.branches
  );
  const authUser = useSelector(
    (state: IAppState) => state.authReducer.authUser
  );
  const country = organisation!.address.country;
  const {t} = useTranslation();
  const [loadBooking, { loading: bookingDataLoading }] = useLazyQuery(
    GET_BOOKING,
    {
      fetchPolicy: "network-only",
      onCompleted: ({ consumerGetBooking }) => {
        if (
          consumerGetBooking &&
          consumerGetBooking.testDriveBookingDetails &&
          branchData
        ) {
          if (
            !consumerGetBooking.currentBookingSchedules ||
            consumerGetBooking.currentBookingSchedules.length === 0
          ) {
            socket.emit(SOCKET_EVENTS.REQUEST_VIEW_BOOKING, {
              bookingId: consumerGetBooking.id,
            });
          }
          const selectedBranch = branchData?.find(
            (branch) => branch.id === consumerGetBooking.branchId
          );
          const branchAddress = consumerGetBooking.branch?.address?.fullAddress || selectedBranch?.address?.fullAddress || t("root:na");
          const scheduleVehicle =
            consumerGetBooking.currentBookingSchedules?.[0]?.vehicle;
          const vehicle =
            scheduleVehicle?.make && scheduleVehicle?.model
              ? `${scheduleVehicle.make} ${scheduleVehicle.model}`
              : t("root:na");
          const testDriveBookingSummary = {
            id: consumerGetBooking.id,
            vehicle,
            testDriveBookingDetails: consumerGetBooking.testDriveBookingDetails,
            bookingTime: d
              .fromISO(consumerGetBooking.pickupDateTime)
              .setZone(selectedBranch?.timeZone)
              .toFormat("dd LLLL yyyy 'at' hh':'mm a"),
            bookingReference: consumerGetBooking.referenceNumber,
            branchName: selectedBranch?.name || t("root:na"),
            branchAddress: branchAddress,
            customer: consumerGetBooking.customer,
            pickupOtherLocation: consumerGetBooking.pickupOtherLocation,
          };
          if (consumerGetBooking.customerType === CustomerType.BUSINESS) {
            testDriveBookingSummary.customer = {
              firstName: consumerGetBooking.businessCustomer.contact.firstName,
              lastName: consumerGetBooking.businessCustomer.contact.lastName,
              phoneNumber:
                consumerGetBooking.businessCustomer.contact.phoneNumber,
              email: consumerGetBooking.businessCustomer.contact.email,
              location: consumerGetBooking.businessCustomer.registeredAddress,
            };
          } else {
            testDriveBookingSummary.customer = consumerGetBooking.customer;
          }
          setTestDriveBookingSummary(testDriveBookingSummary);
        }
      },
    }
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const bookingId = params.get("id");
    if (bookingId) {
      socket.emit(SOCKET_EVENTS.REQUEST_VIEW_BOOKING, { bookingId: bookingId });
      return loadBooking({
        variables: {
          bookingId: bookingId,
        },
      });
    }
  }, [loadBooking, location]);

  useEffect(() => {
    socket.auth = {
      userId: authUser?._id,
    };
    socket.connect();
    return () => {
      socket.disconnect();
    };
  }, [authUser?._id]);

  useEffect(() => {
    socket.on(SOCKET_EVENTS.GET_UPDATED_BOOKING, (data) => {
      loadBooking({
        variables: {
          bookingId: data.bookingId,
        },
      });
    });

    return () => {
      socket.off(SOCKET_EVENTS.GET_UPDATED_BOOKING);
    };
  }, [loadBooking]);

  if (!testDriveBookingSummary || bookingDataLoading) {
    return (
      <div
        style={{
          height: 400,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  return (
    <div>
      <TopBar height={70} logoSize={44} />
      <Container style={{ marginTop: 75 }}>
        <Grid item container xs={12}>
          <Paper className={classes.root} elevation={0}>
            <Typography variant="h1" className={classes.header}>
              {getLocalizedBookingSyntex(country)} {t("root:confirmation")}
            </Typography>

            <Box className={classes.section}>
              <Typography variant="h3" gutterBottom>
                {t("root:your")} {getLocalizedBookingSyntex(country)} {t("root:details")}
              </Typography>
            </Box>
            <Box className={classes.section}>
              <Grid container spacing={4}>
                <Grid item xs={12} sm={3}>
                  <Typography className={classes.label}>
                    {getLocalizedBookingSyntex(country)} {t("root:reference")}
                  </Typography>
                  <Typography className={classes.value}>
                    {testDriveBookingSummary.bookingReference}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography className={classes.label}>{t("root:model")}</Typography>
                  <Typography className={classes.value}>
                    {testDriveBookingSummary.vehicle}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography className={classes.label}>
                    {t("root:bookingType")}
                  </Typography>
                  <Typography className={classes.value}>
                    {testDriveBookingSummary.testDriveBookingDetails.type}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography className={classes.label}>
                    {t("root:bookingTime")}
                  </Typography>
                  <Typography className={classes.value}>
                    {testDriveBookingSummary.bookingTime}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Box className={classes.section}>
              <Grid container spacing={4}>
                <Grid item xs={12} sm={3}>
                  <Typography className={classes.label}>{t("root:experience")}</Typography>
                  <Typography className={classes.value}>
                    {testDriveBookingSummary.testDriveBookingDetails.experience}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography className={classes.label}>{t("root:dealership")}</Typography>
                  <Typography className={classes.value}>
                    {testDriveBookingSummary.branchName}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography className={classes.label}>
                    {t("root:dealershipAddress")}
                  </Typography>
                  <Typography className={classes.value}>
                    {testDriveBookingSummary.branchAddress}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Box className={classes.section}>
              <Typography variant="h3" gutterBottom>
                {t("root:customerDetails")}
              </Typography>
            </Box>

            <Box className={classes.section}>
              <Grid container spacing={4}>
                <Grid item xs={12} sm={3}>
                  <Typography className={classes.label}>{t("root:customer")}</Typography>
                  <Typography className={classes.value}>
                    {testDriveBookingSummary.customer.firstName}{" "}
                    {testDriveBookingSummary.customer.lastName}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography className={classes.label}>{t("root:phone")}</Typography>
                  <Typography className={classes.value}>
                    {testDriveBookingSummary.customer.phoneNumber.phone}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography className={classes.label}>{t("root:address")}</Typography>
                  <Typography className={classes.value}>
                    {testDriveBookingSummary.customer.location.fullAddress}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography className={classes.label}>{t("root:email")}</Typography>
                  <Typography className={classes.value}>
                    {testDriveBookingSummary.customer.email}
                  </Typography>
                </Grid>
                {testDriveBookingSummary.pickupOtherLocation?.fullAddress && (
                  <>
                  <Grid item xs={12} sm={3}>
                  <Typography className={classes.label}>
                    {t("root:deliveryAddress")}
                  </Typography>
                  <Typography className={classes.value}>
                    {testDriveBookingSummary.pickupOtherLocation?.fullAddress}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography className={classes.label}>
                    {t("root:collectionAddress")}
                  </Typography>
                  <Typography className={classes.value}>
                    {testDriveBookingSummary.pickupOtherLocation?.fullAddress}
                  </Typography>
                </Grid>
                  </>
                )}
              </Grid>
            </Box>
          </Paper>
        </Grid>
        <Grid item container xs={12} className={classes.bottomBar}>
          <Fab
            aria-label="return-home"
            variant="extended"
            size="medium"
            onClick={() => {
              history.push(`/`);
            }}
            style={{
              backgroundColor: "var(--primary-color)",
            }}
          >
            {`${t("root:new")} ${getLocalizedBookingSyntex(country)}`}
          </Fab>
        </Grid>
      </Container>
    </div>
  );
};

export default TestDriveBookingConfirmation;
